<template>
  <div class="w-full min-h-16 mt-2 bg-white rounded-md flex flex-row">
    <div
      class="w-2 rounded-l-md"
      :class="notificationBorderColor"
    />
    <div
      class="w-full pl-4 p-1 bg-input border border-input rounded-r-md text-table"
      @click="handleClick"
    >
      <div class="flex flex-row justify-between">
        <p class="text-md font-bold">
          {{ t(notification.title) }}
        </p>
        <hit-icon
          v-if="clearable"
          icon="clear"
          size="sm"
          :clickable="true"
          @click.stop="
            $emit(
              'clear',
              notification.notification_id,
              notification?.document_id
            )
          "
        />
      </div>
      <p
        v-if="notification.body_is_html"
        v-html="convertNotificationBody(notification.body)"
      />
      <p v-else>
        {{ convertNotificationBody(notification.body) }}
      </p>
      <div class="flex flex-row justify-between">
        <div class="flex flex-row gap-2">
          <hit-button
            v-if="notification.document_id"
            color="accent"
            prefix-icon="preview"
            icon-size="base"
            :remove-padding="true"
            :tooltip="t('hit-app.common.preview')"
            @click.stop="$emit('preview', notification.document_id)"
          />
          <hit-button
            v-if="notification.document_id"
            color="accent"
            prefix-icon="download"
            icon-size="base"
            :remove-padding="true"
            :tooltip="t('hit-app.common.download')"
            @click.stop="$emit('download', notification.document_id)"
          />
        </div>
        <p class="flex justify-end text-[0.65rem]">
          <hit-date
            v-if="notification.deposit_date"
            :value="new Date(notification.deposit_date)"
            :show-only-time="showOnlyTime"
          />
        </p>
      </div>
    </div>
  </div>
</template>
<script>
import HitIcon from '../icon/HitIcon.vue';
import {useI18n} from 'vue-i18n';
import HitBreakpointsMixin from '@hit/components/src/mixins/breakpoints/HitBreakpointsMixin';
import HitDate from '../date/HitDate.vue';
import HitButton from '../button/HitButton.vue';

export default {
  name: 'HitNotification',
  components: {
    HitIcon,
    HitDate,
    HitButton,
  },
  mixins: [HitBreakpointsMixin],
  props: {
    notification: {
      type: Object,
      required: true,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    actionService: {
      type: Function,
      required: false,
      default: null,
    },
  },
  setup() {
    const {t} = useI18n();
    return {t};
  },
  computed: {
    showOnlyTime() {
      const today = new Date();
      const notificationDate = new Date(this.notification.deposit_date);
      return (
        today.getDate() === notificationDate.getDate() &&
        today.getMonth() === notificationDate.getMonth() &&
        today.getFullYear() === notificationDate.getFullYear()
      );
    },
    isClickable() {
      try {
        return 'click_event' in this.notification.body;
      } catch {
        return false;
      }
    },
    notificationBorderColor() {
      if (this.notification?.type.toUpperCase() === 'TASK') {
        switch (this.notification?.progress) {
          case 100:
            return 'bg-accent';
          case -1:
            return 'bg-danger';
          default:
            return 'bg-header';
        }
      }
      return this.notification?.read ? 'bg-header' : 'bg-accent';
    },
  },
  methods: {
    convertNotificationBody(notificationBody) {
      if (typeof notificationBody === 'string') {
        // This can either be an i18n key or a string that has to be displayed
        return this.t(notificationBody);
      } else {
        return this.t(notificationBody.message, notificationBody?.payload);
      }
    },

    handleClick() {
      if (!this.isClickable) return;
      const clickData = this.notification.body.click_event;
      switch (clickData.type) {
        case 'redirect':
          this.$router.push({
            name: clickData.target.name,
            params: clickData.target.params,
            query: clickData.target.query,
          });
          break;

        case 'action_call':
          if (!this.actionService) {
            throw Error(
              'The action service needs to be passed to the nofication component because this component is in the hit/components module'
            );
          }
          if (clickData.method === 'GET') {
            this.actionService.get(
              clickData.route,
              clickData.target?.config ?? {},
              clickData.download_as_pdf ?? false
            );
          } else if (clickData.method === 'POST') {
            this.actionService.post(clickData.route, {
              values: clickData.target?.values ?? {},
              params: clickData.target?.params ?? {},
              config: clickData.target?.config ?? {},
            });
          }
          break;

        default:
          throw Error(`Unsupported click event type ${clickData.type}`);
      }
    },
  },
};
</script>
